import React from "react";
import { Card, Progress, Typography, Space, Row, Col, Tooltip } from "antd";
import {
  HomeOutlined,
  CoffeeOutlined,
  BulbOutlined,
  ExperimentOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import CustomCard from "../../blocks/Card/Card";

const { Title, Text, Paragraph } = Typography;

const PropertySummary = () => {
  const rooms = [
    {
      name: "Overall Home",
      icon: <HomeOutlined />,
      quality: 4.86,
      condition: 4.42,
    },
    {
      name: "Kitchen",
      icon: <CoffeeOutlined />,
      quality: 5.32,
      condition: 4.7,
    },
    { name: "Bedroom", icon: <BulbOutlined />, quality: 4.64, condition: 4.2 },
    {
      name: "Bathroom",
      icon: <ExperimentOutlined />,
      quality: 3.36,
      condition: 2.34,
    },
  ];

  const getScoreDescription = (score) => {
    if (score >= 5) return "Excellent";
    if (score >= 4) return "Very Good";
    if (score >= 3) return "Good";
    if (score >= 2) return "Fair";
    return "Needs Work";
  };

  const getScoreColor = (score) => {
    if (score >= 5) return "#52c41a";
    if (score >= 4) return "#a0d911";
    if (score >= 3) return "#fadb14";
    if (score >= 2) return "#fa8c16";
    return "#f5222d";
  };

  const ScorePane = ({ title, description, scores }) => (
    <Card
      title={<Title level={5}>{title}</Title>}
      extra={
        <Tooltip title={description}>
          <InfoCircleOutlined />
        </Tooltip>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {rooms.map((room) => (
          <div key={room.name}>
            <Space align="center">
              {room.icon}
              <Text strong>{room.name}</Text>
            </Space>
            <div style={{ marginTop: 8 }}>
              <Space style={{ width: "100%", justifyContent: "space-between" }}>
                <Text>{getScoreDescription(scores[room.name])}</Text>
                <Text strong>{scores[room.name].toFixed(1)} / 6</Text>
              </Space>
              <Progress
                percent={(scores[room.name] / 6) * 100}
                strokeColor={getScoreColor(scores[room.name])}
                showInfo={false}
              />
            </div>
          </div>
        ))}
      </Space>
    </Card>
  );

  return (
    <CustomCard
      title={<Title level={4}>How's This Home Doing?</Title>}
      className="card-height"
    >
      <Paragraph type="secondary">
        We've assessed the home on two factors: Quality (how nice it is) and
        Condition (how well it's holding up). Both are scored from 0 to 6, with
        higher scores being better.
      </Paragraph>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <ScorePane
            title="Quality Scores"
            description="Quality refers to how nice or high-end something is."
            scores={Object.fromEntries(
              rooms.map((room) => [room.name, room.quality])
            )}
          />
        </Col>
        <Col xs={24} sm={12}>
          <ScorePane
            title="Condition Scores"
            description="Condition is about how well it's been maintained."
            scores={Object.fromEntries(
              rooms.map((room) => [room.name, room.condition])
            )}
          />
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        <Title level={5}>What do these scores mean?</Title>
        <Row gutter={16}>
          <Col span={12}>
            <ul>
              <li>
                <Text strong>5-6:</Text> Excellent - Top-notch!
              </li>
              <li>
                <Text strong>4-5:</Text> Very Good - Above average
              </li>
              <li>
                <Text strong>3-4:</Text> Good - Satisfactory
              </li>
            </ul>
          </Col>
          <Col span={12}>
            <ul>
              <li>
                <Text strong>2-3:</Text> Fair - Could be better
              </li>
              <li>
                <Text strong>0-2:</Text> Needs Work - Requires attention
              </li>
            </ul>
          </Col>
        </Row>
      </Card>

      <Paragraph style={{ marginTop: 16 }}>
        <Text strong>Key Takeaways:</Text> The kitchen stands out with high
        scores in both quality and condition. The bathroom might benefit from
        some updates to match the rest of the house, particularly in terms of
        condition.
      </Paragraph>
    </CustomCard>
  );
};

export default PropertySummary;
